/* ==========================================================================
   Product Support page
   Custom element: <product-support-page>

   Architecture (see docs):
   - Left navigator is rendered from a Shopify menu (Category > Series > Product)
   - Right side renders one full panel per product (pre-rendered, hidden)
   - Product content comes from product metafields (metaobjects)
   - This script only manages STATE: current product switching, navigator
     accordion, FAQ / video category filters, video dialog, anchor nav.

   All clicks are handled through ONE delegated listener on the root
   element, so interactions keep working even if the markup is re-rendered
   or injected after this script loads.

   Progressive enhancement only - the server-rendered HTML shows the first
   product without JavaScript.
   ========================================================================== */

if (!customElements.get('product-support-page')) {
  customElements.define(
    'product-support-page',
    class ProductSupportPage extends HTMLElement {
      constructor() {
        super();
        this.activeProductHandle = '';
        this.lastVideoTrigger = null;
        this.navObserver = null;
        this.handleClick = this.handleClick.bind(this);
      }

      connectedCallback() {
        if (this.dataset.psReady === 'true') return;
        this.dataset.psReady = 'true';

        this.nav = this.querySelector('[data-ps-nav]');
        this.aside = this.querySelector('.product-support__aside');
        this.navTrigger = this.querySelector('[data-ps-nav-trigger]');
        this.navBackdrop = this.querySelector('[data-ps-nav-backdrop]');
        this.panels = Array.from(this.querySelectorAll('[data-ps-panel]'));
        this.contentArea = this.querySelector('[data-ps-content]');
        this.dockLinks = Array.from(this.querySelectorAll('[data-ps-docknav] [data-ps-nav-link]'));
        this.dialog = this.querySelector('[data-ps-dialog]');
        this.dialogMedia = this.querySelector('[data-ps-dialog-media]');
        this.dialogError = this.querySelector('[data-ps-dialog-error]');

        this.addEventListener('click', this.handleClick);
        this.initDialog();
        this.initFromUrl();
      }

      /* ---------------- Delegated click handling ---------------- */

      handleClick(event) {
        if (event.defaultPrevented) return;

        /* Mobile navigator drawer: open via trigger, close via backdrop */
        const navTrigger = event.target.closest('[data-ps-nav-trigger]');
        if (navTrigger) {
          this.toggleMobileNav();
          return;
        }
        const navBackdrop = event.target.closest('[data-ps-nav-backdrop]');
        if (navBackdrop) {
          this.closeMobileNav();
          return;
        }

        /* Navigator accordion runs on native <details>/<summary> -
           no JavaScript needed. Product selection in the navigator:
           real <a> links (?product=) intercepted for panel switching;
           without JavaScript the browser navigates and the server
           renders the matching panel. */
        const productBtn = event.target.closest('[data-ps-nav-product]');
        if (productBtn) {
          event.preventDefault();
          this.selectProduct(productBtn.dataset.productHandle || '', {
            title: productBtn.dataset.productTitle || '',
            track: true,
          });
          return;
        }

        /* Video cards open the dialog instead of navigating */
        const videoCard = event.target.closest('[data-ps-video-card]');
        if (videoCard) {
          event.preventDefault();
          this.lastVideoTrigger = videoCard;
          this.openVideoDialog(videoCard);
          return;
        }

        /* FAQ / video category chips */
        const chip = event.target.closest('[data-ps-faq-chip], [data-ps-video-chip]');
        if (chip) {
          this.applyChip(chip);
          return;
        }

        /* Dialog close button */
        const closeBtn = event.target.closest('[data-ps-dialog-close]');
        if (closeBtn) {
          this.closeVideoDialog();
          return;
        }

        /* Analytics only (default navigation continues) */
        const manual = event.target.closest('[data-ps-manual-download]');
        if (manual) {
          this.track({
            event: 'support_manual_download',
            product_handle: manual.dataset.productHandle || '',
            document_title: manual.dataset.docTitle || '',
          });
          return;
        }

        const productLink = event.target.closest('[data-ps-product-link]');
        if (productLink) {
          this.track({
            event: 'support_product_page_click',
            product_handle: productLink.dataset.productHandle || '',
          });
        }
      }

      /* ---------------- Utilities ---------------- */

      track(payload) {
        try {
          window.dataLayer = window.dataLayer || [];
          window.dataLayer.push(payload);
        } catch (error) {
          /* Analytics must never break the page */
        }
      }

      prefersReducedMotion() {
        return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      }

      isMobileViewport() {
        return window.matchMedia ? window.matchMedia('(max-width: 1023px)').matches : false;
      }

      /* ---------------- Navigator accordion ---------------- */

      setGroupOpen(group, open) {
        /* Accordion groups are native <details> elements */
        group.open = open;
      }

      /* ---------------- Mobile navigator drawer ---------------- */

      toggleMobileNav() {
        if (!this.aside) return;
        this.aside.classList.contains('is-open')
          ? this.closeMobileNav()
          : this.openMobileNav();
      }

      openMobileNav() {
        if (!this.aside) return;
        this.aside.classList.add('is-open');
        if (this.navBackdrop) this.navBackdrop.hidden = false;
        if (this.navTrigger) this.navTrigger.setAttribute('aria-expanded', 'true');
      }

      closeMobileNav() {
        if (!this.aside) return;
        this.aside.classList.remove('is-open');
        if (this.navBackdrop) this.navBackdrop.hidden = true;
        if (this.navTrigger) this.navTrigger.setAttribute('aria-expanded', 'false');
      }

      expandNavPath(handle) {
        if (!this.nav) return;
        const escaped = window.CSS && CSS.escape ? CSS.escape(handle) : handle;
        const button = this.nav.querySelector('[data-ps-nav-product][data-product-handle="' + escaped + '"]');
        if (!button) return;
        let group = button.closest('[data-ps-nav-group]');
        while (group && group !== this.nav) {
          this.setGroupOpen(group, true);
          group = group.parentElement ? group.parentElement.closest('[data-ps-nav-group]') : null;
        }
      }

      /* ---------------- Product selection ---------------- */

      selectProduct(handle, options = {}) {
        const panel = this.panels.find((p) => p.dataset.handle === handle);
        if (!panel) return;

        this.activeProductHandle = handle;

        /* Close the mobile drawer after picking a product */
        this.closeMobileNav();

        /* Toggle panels */
        this.panels.forEach((p) => {
          p.hidden = p.dataset.handle !== handle;
        });

        /* Navigator active state */
        this.querySelectorAll('[data-ps-nav-product]').forEach((button) => {
          const isActive = button.dataset.productHandle === handle;
          button.classList.toggle('is-active', isActive);
          if (isActive) {
            button.setAttribute('aria-current', 'true');
          } else {
            button.removeAttribute('aria-current');
          }
        });
        this.expandNavPath(handle);

        /* Anchor nav: rewrite hrefs to the blocks of this panel,
           hide links whose block does not exist for this product */
        this.dockLinks.forEach((link) => {
          const key = link.dataset.sectionKey || '';
          const targetId = 'ps-' + key + '-' + handle;
          const target = document.getElementById(targetId);
          link.setAttribute('href', '#' + targetId);
          link.hidden = !target;
        });
        const docknav = this.querySelector('[data-ps-docknav]');
        if (docknav) {
          const hasAnyTarget = this.dockLinks.some((link) => !link.hidden);
          docknav.classList.toggle('is-visible', hasAnyTarget);
        }
        this.observeNavSections();

        this.updateUrl(handle);

        if (options.track !== false) {
          this.track({
            event: 'support_product_selected',
            product_handle: handle,
            product_title: options.title || panel.dataset.title || '',
          });
        }

        /* On mobile the navigator sits above the content: scroll to it */
        if (this.isMobileViewport() && this.contentArea) {
          const behavior = this.prefersReducedMotion() ? 'auto' : 'smooth';
          this.contentArea.scrollIntoView({ behavior: behavior, block: 'start' });
        }
      }

      updateUrl(handle) {
        if (!window.history || !window.history.replaceState) return;
        try {
          const url = new URL(window.location.href);
          if (handle) {
            url.searchParams.set('product', handle);
          } else {
            url.searchParams.delete('product');
          }
          window.history.replaceState({}, '', url);
        } catch (error) {
          /* URL sync is an enhancement - ignore failures */
        }
      }

      initFromUrl() {
        let handle = '';
        try {
          const params = new URLSearchParams(window.location.search);
          handle = (params.get('product') || '').trim();
        } catch (error) {
          handle = '';
        }
        const valid = this.panels.some((p) => p.dataset.handle === handle);
        const initial = valid ? handle : this.panels.length ? this.panels[0].dataset.handle : '';
        if (initial) {
          const trackDefault = handle !== '' && valid;
          this.selectProduct(initial, { track: trackDefault });
        }
      }

      /* ---------------- FAQ / Video in-panel category filters ---------------- */

      applyChip(chip) {
        const isFaq = chip.hasAttribute('data-ps-faq-chip');
        const section = chip.closest(isFaq ? '[data-ps-faq-section]' : '[data-ps-video-section]');
        if (!section) return;

        const chips = Array.from(
          section.querySelectorAll(isFaq ? '[data-ps-faq-chip]' : '[data-ps-video-chip]')
        );
        const items = Array.from(
          section.querySelectorAll(isFaq ? '[data-ps-faq-item]' : '[data-ps-video-card]')
        );
        const emptyNote = section.querySelector(
          isFaq ? '[data-ps-faq-empty]' : '[data-ps-video-empty]'
        );

        const value = chip.dataset.value || '';
        chips.forEach((c) => {
          const isActive = c === chip;
          c.classList.toggle('is-active', isActive);
          c.setAttribute('aria-pressed', isActive ? 'true' : 'false');
        });

        let visible = 0;
        items.forEach((item) => {
          const show = !value || item.dataset.filterCategory === value;
          item.hidden = !show;
          if (show) visible++;
        });
        if (emptyNote) emptyNote.classList.toggle('is-visible', visible === 0);
      }

      /* ---------------- Anchor nav highlight ---------------- */

      observeNavSections() {
        const activePanel = this.panels.find((p) => !p.hidden);
        if (!activePanel || !this.dockLinks.length) return;

        const sections = this.dockLinks
          .map((link) => {
            const id = (link.getAttribute('href') || '').replace('#', '');
            return id ? document.getElementById(id) : null;
          })
          .filter(Boolean);
        if (!sections.length) return;

        if (this.navObserver) this.navObserver.disconnect();
        if (!('IntersectionObserver' in window)) return;

        this.navObserver = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (!entry.isIntersecting) return;
              this.dockLinks.forEach((link) => {
                link.classList.toggle(
                  'is-active',
                  link.getAttribute('href') === '#' + entry.target.id
                );
              });
            });
          },
          { rootMargin: '-30% 0px -60% 0px', threshold: 0 }
        );
        sections.forEach((section) => this.navObserver.observe(section));
      }

      /* ---------------- Video dialog ---------------- */

      initDialog() {
        if (!this.dialog) return;
        this.supportsNativeDialog = typeof this.dialog.showModal === 'function';
        if (!this.supportsNativeDialog) {
          this.dialog.classList.add('no-native');
          this.dialogBackdrop = document.createElement('div');
          this.dialogBackdrop.className = 'product-support__dialog-backdrop';
          this.dialogBackdrop.hidden = true;
          document.body.appendChild(this.dialogBackdrop);
          this.dialogBackdrop.addEventListener('click', () => this.closeVideoDialog());
        }

        this.dialog.addEventListener('close', () => this.destroyDialogMedia());
        /* Backdrop click closes (native dialog) */
        this.dialog.addEventListener('click', (event) => {
          if (this.supportsNativeDialog && event.target === this.dialog) {
            this.closeVideoDialog();
          }
        });
      }

      openVideoDialog(card) {
        if (!this.dialog) return;
        if (this.dialogError) this.dialogError.classList.remove('is-visible');
        this.destroyDialogMedia();

        const externalUrl = card.dataset.videoUrl || '';
        const hostedUrl = card.dataset.hostedUrl || '';
        let inserted = false;

        try {
          if (externalUrl) {
            inserted = this.insertEmbed(externalUrl);
          } else if (hostedUrl) {
            const video = document.createElement('video');
            video.controls = true;
            video.autoplay = true;
            video.playsInline = true;
            video.src = hostedUrl;
            this.dialogMedia.appendChild(video);
            inserted = true;
          }
        } catch (error) {
          inserted = false;
        }

        if (!inserted) {
          if (this.dialogError) this.dialogError.classList.add('is-visible');
          if (window.console) console.warn('[product-support] Unable to play video:', externalUrl || hostedUrl);
        }

        if (this.supportsNativeDialog) {
          this.dialog.showModal();
        } else {
          this.dialog.setAttribute('open', '');
          this.dialogBackdrop.hidden = false;
        }

        this.track({
          event: 'support_video_play',
          product_handle: card.dataset.productHandle || '',
          video_title: card.dataset.videoTitle || '',
        });
      }

      insertEmbed(url) {
        /* YouTube */
        const yt = url.match(
          /(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([A-Za-z0-9_-]{6,})/
        );
        if (yt) {
          const iframe = document.createElement('iframe');
          iframe.allow = 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture';
          iframe.allowFullscreen = true;
          iframe.title = 'YouTube video player';
          iframe.src = 'https://www.youtube-nocookie.com/embed/' + yt[1] + '?autoplay=1&rel=0';
          this.dialogMedia.appendChild(iframe);
          return true;
        }
        /* Vimeo */
        const vimeo = url.match(/vimeo\.com\/(?:video\/)?(\d+)/);
        if (vimeo) {
          const iframe = document.createElement('iframe');
          iframe.allow = 'autoplay; fullscreen; picture-in-picture';
          iframe.allowFullscreen = true;
          iframe.title = 'Vimeo video player';
          iframe.src = 'https://player.vimeo.com/video/' + vimeo[1] + '?autoplay=1';
          this.dialogMedia.appendChild(iframe);
          return true;
        }
        /* Direct mp4/webm URL */
        if (/\.(mp4|webm|ogg|mov)(\?|$)/i.test(url)) {
          const video = document.createElement('video');
          video.controls = true;
          video.autoplay = true;
          video.playsInline = true;
          video.src = url;
          this.dialogMedia.appendChild(video);
          return true;
        }
        return false;
      }

      closeVideoDialog() {
        if (!this.dialog) return;
        if (this.supportsNativeDialog) {
          this.dialog.close();
        } else {
          this.dialog.removeAttribute('open');
          if (this.dialogBackdrop) this.dialogBackdrop.hidden = true;
          this.destroyDialogMedia();
        }
      }

      destroyDialogMedia() {
        if (!this.dialogMedia) return;
        /* Removing nodes stops hosted <video> playback and unloads iframes */
        while (this.dialogMedia.firstChild) {
          this.dialogMedia.removeChild(this.dialogMedia.firstChild);
        }
        if (this.dialogError) this.dialogError.classList.remove('is-visible');
        /* Return focus to the card that opened the dialog */
        if (this.lastVideoTrigger && typeof this.lastVideoTrigger.focus === 'function') {
          this.lastVideoTrigger.focus();
          this.lastVideoTrigger = null;
        }
      }
    }
  );
}
